import { useRef, useState, type KeyboardEvent } from "react";
import { Layers, Droplets, Flame, type LucideIcon } from "lucide-react";
import { ServicesGrid } from "@/components/site-blocks";
import {
  CATEGORY_ORDER,
  CATEGORY_LABELS,
  CATEGORY_DESCRIPTIONS,
  SERVICES_BY_CATEGORY,
  type ServiceCategory,
} from "@/lib/services";

const CATEGORY_ICONS: Record<ServiceCategory, LucideIcon> = {
  insulation: Layers,
  structural: Droplets,
  heating: Flame,
};

export function HomeServicesAccordion() {
  const [open, setOpen] = useState<number | null>(null);
  const btnRefs = useRef<Array<HTMLButtonElement | null>>([]);

  function onKey(e: KeyboardEvent<HTMLButtonElement>, i: number) {
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      const next = (i + 1) % CATEGORY_ORDER.length;
      setOpen(next);
      btnRefs.current[next]?.focus();
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      const prev = (i - 1 + CATEGORY_ORDER.length) % CATEGORY_ORDER.length;
      setOpen(prev);
      btnRefs.current[prev]?.focus();
    } else if (e.key === "Home") {
      e.preventDefault();
      setOpen(0);
      btnRefs.current[0]?.focus();
    } else if (e.key === "End") {
      e.preventDefault();
      const last = CATEGORY_ORDER.length - 1;
      setOpen(last);
      btnRefs.current[last]?.focus();
    }
  }

  return (
    <div className="space-y-2 md:space-y-3" role="tablist" aria-label="Our services by category">
      {CATEGORY_ORDER.map((category, i) => {
        const isOpen = open === i;
        const overlay = i % 2 === 0 ? "bg-primary" : "bg-accent";
        const Icon = CATEGORY_ICONS[category];
        const services = SERVICES_BY_CATEGORY[category];
        const hero = services[0]?.hero800 ?? services[0]?.hero ?? "";

        return (
          <div
            key={category}
            className={`overflow-hidden rounded-lg md:rounded-xl border transition-colors duration-300 ${
              isOpen ? "border-border" : "border-transparent"
            }`}
          >
            <button
              ref={(el) => {
                btnRefs.current[i] = el;
              }}
              role="tab"
              aria-expanded={isOpen}
              aria-selected={isOpen}
              aria-controls={`svc-panel-${category}`}
              id={`svc-tab-${category}`}
              onClick={() => setOpen(isOpen ? null : i)}
              onKeyDown={(e) => onKey(e, i)}
              className="group relative w-full h-16 md:h-20 overflow-hidden text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:z-10"
            >
              <img
                src={hero}
                alt=""
                aria-hidden
                loading="lazy"
                decoding="async"
                className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
              />
              <div
                className={`absolute inset-0 ${overlay} transition-opacity duration-300 ease-out ${
                  isOpen ? "opacity-[0.82]" : "opacity-[0.72] group-hover:opacity-[0.6]"
                }`}
                aria-hidden
              />
              <div className="relative flex items-center gap-3 md:gap-4 px-4 md:px-6 h-full text-primary-foreground">
                <span className="inline-flex h-8 w-8 md:h-10 md:w-10 items-center justify-center rounded-full bg-white/15 backdrop-blur-sm shrink-0 transition-transform duration-300 ease-out group-hover:scale-110">
                  <Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden />
                </span>
                <span className="font-medium text-sm md:text-base">{CATEGORY_LABELS[category]}</span>
                <span
                  className={`ml-auto text-xs md:text-sm opacity-80 transition-transform duration-300 ease-out ${
                    isOpen ? "rotate-90" : "group-hover:translate-x-0.5"
                  }`}
                  aria-hidden
                >
                  ▸
                </span>
              </div>
            </button>
            <div
              className="grid transition-[grid-template-rows] duration-300 ease-out"
              style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}
            >
              <div className="overflow-hidden">
                <div
                  id={`svc-panel-${category}`}
                  role="tabpanel"
                  aria-labelledby={`svc-tab-${category}`}
                  aria-hidden={!isOpen}
                  className="bg-background px-4 md:px-6 py-4 md:py-5"
                >
                  <p className="text-sm md:text-base text-foreground/85 leading-relaxed mb-5 md:mb-6">
                    {CATEGORY_DESCRIPTIONS[category]}
                  </p>
                  <ServicesGrid services={services} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
